import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Terminal, Users, Plus, Lock, ChevronRight, 
  X, Copy, Dumbbell, UserPlus, ChevronDown
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const MainPage = () => {
  const { 
    username, userId, users, isLoggedIn, 
    addUser, switchUser, removeUser, logoutAll 
  } = useUser();
  const navigate = useNavigate();
  
  const [inputName, setInputName] = useState("");
  const [lobbies, setLobbies] = useState([]);
  const [myLobbies, setMyLobbies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", password: "" });
  const [joinForm, setJoinForm] = useState({ lobbyId: "", password: "" });
  const [newUserName, setNewUserName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchLobbies();
    if (isLoggedIn) {
      fetchMyLobbies();
    }
  }, [isLoggedIn, userId]);

  const fetchLobbies = async () => {
    try {
      const res = await axios.get(`${API}/lobbies`);
      setLobbies(res.data);
    } catch (err) {
      console.error("Failed to load lobbies");
    }
  };

  const fetchMyLobbies = async () => {
    if (!userId) return;
    try {
      const res = await axios.get(`${API}/lobbies/user/${userId}`);
      setMyLobbies(res.data);
    } catch (err) {
      console.error("Failed to load my lobbies");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!inputName.trim() || inputName.trim().length < 2) {
      toast.error("Username must be at least 2 characters");
      return;
    }
    setLoading(true);
    try {
      await addUser(inputName.trim());
      toast.success(`Welcome, ${inputName.trim()}!`);
      setInputName("");
    } catch (err) {
      toast.error("Failed to login");
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUserName.trim() || newUserName.trim().length < 2) {
      toast.error("Username must be at least 2 characters");
      return;
    }
    setLoading(true);
    try {
      await addUser(newUserName.trim());
      toast.success(`Added ${newUserName.trim()}!`);
      setNewUserName("");
      setAddUserOpen(false);
    } catch (err) {
      toast.error("Failed to add user");
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchUser = (user) => {
    switchUser(user.id);
    toast.success(`Switched to ${user.username}`);
  };

  const handleRemoveUser = (user, e) => {
    e.stopPropagation();
    removeUser(user.id);
    toast.success(`Removed ${user.username}`);
  };

  const handleCreateLobby = async (e) => {
    e.preventDefault();
    if (!createForm.name || !createForm.password) {
      toast.error("Name and password required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/lobbies?user_id=${userId}`, createForm);
      const lobbyId = res.data.id;
      setCreateOpen(false);
      setCreateForm({ name: "", password: "" });
      toast.success("Lobby created!");
      // Delay navigation to let dialog overlay unmount
      setTimeout(() => navigate(`/lobby/${lobbyId}`), 150);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create lobby");
    } finally {
      setSubmitting(false);
    }
  };

  const handleJoinLobby = async (e) => {
    e.preventDefault();
    if (!joinForm.lobbyId || !joinForm.password) {
      toast.error("Lobby ID and password required");
      return;
    }
    setSubmitting(true);
    try {
      const lobbyId = joinForm.lobbyId;
      await axios.post(`${API}/lobbies/${lobbyId}/join?user_id=${userId}`, { password: joinForm.password });
      setJoinOpen(false);
      setJoinForm({ lobbyId: "", password: "" });
      toast.success("Joined lobby!");
      // Delay navigation to let dialog overlay unmount
      setTimeout(() => navigate(`/lobby/${lobbyId}`), 150);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to join lobby");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEnterLobby = (lobbyId) => {
    navigate(`/lobby/${lobbyId}`);
  };

  const copyLobbyId = (id, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    toast.success("Lobby ID copied!");
  };

  return (
    <div className="min-h-screen bg-[#FAF9F7] p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <header className="flex items-center justify-between mb-8 pb-4 border-b border-[rgba(0,0,0,0.1)]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 border-2 border-[#D97706] flex items-center justify-center">
              <Terminal className="w-4 h-4 text-[#D97706]" />
            </div>
            <h1 className="font-['VT323'] text-2xl md:text-3xl text-[#D97706] tracking-wide">
              Working.exe
            </h1>
          </div>
          <div className="text-xs text-[#8C8C89] font-mono">v1.0.0</div>
        </header>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Left Panel - User Login / Switcher */}
          <div className="lg:col-span-4">
            <div className="retro-box p-5">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[rgba(0,0,0,0.08)]">
                <Terminal className="w-4 h-4 text-[#D97706]" />
                <span className="font-mono text-xs uppercase tracking-wider text-[#8C8C89]">
                  {isLoggedIn ? "ACTIVE USER" : "ENTER USERNAME"}
                </span>
              </div>

              {!isLoggedIn ? (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="block font-mono text-xs text-[#8C8C89] mb-2">
                      {">"} username_
                    </label>
                    <Input
                      value={inputName}
                      onChange={(e) => setInputName(e.target.value)}
                      placeholder="enter name..."
                      className="h-11 bg-[#FAF9F7] border-[rgba(0,0,0,0.15)] text-[#D97706] font-mono placeholder:text-[#8C8C89] focus-visible:ring-[#D97706] focus-visible:border-[#D97706]"
                      data-testid="username-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-10 retro-btn font-mono text-sm disabled:opacity-50"
                    data-testid="login-btn"
                  >
                    {loading ? "CONNECTING..." : "[ENTER]"}
                  </button>
                </form>
              ) : (
                <div className="space-y-4">
                  {/* User Switcher Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button 
                        className="w-full p-3 bg-[#FAF9F7] border border-[rgba(0,0,0,0.1)] hover:border-[#D97706] transition-colors flex items-center justify-between group"
                        data-testid="user-switcher"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 border-2 border-[#D97706] flex items-center justify-center font-mono text-sm text-[#D97706]">
                            {username.slice(0, 2).toUpperCase()}
                          </div>
                          <div className="text-left">
                            <div className="font-['VT323'] text-xl text-[#D97706]">{username}</div>
                            <div className="font-mono text-xs text-[#8C8C89]">{users.length} user{users.length !== 1 ? 's' : ''} in session</div>
                          </div>
                        </div>
                        <ChevronDown className="w-4 h-4 text-[#8C8C89] group-hover:text-[#D97706] transition-colors" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-64 bg-white border-[rgba(217,119,6,0.3)]" align="start">
                      <div className="px-2 py-1.5">
                        <span className="font-mono text-xs text-[#8C8C89]">SWITCH USER</span>
                      </div>
                      {users.map((user) => (
                        <DropdownMenuItem 
                          key={user.id}
                          onClick={() => handleSwitchUser(user)}
                          className={`font-mono text-sm cursor-pointer flex items-center justify-between group ${
                            user.id === userId ? 'text-[#D97706] bg-[#D97706]/10' : 'text-[#1F1F1E]'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-6 h-6 border flex items-center justify-center text-xs ${
                              user.id === userId ? 'border-[#D97706]' : 'border-[#8C8C89]'
                            }`}>
                              {user.username.slice(0, 2).toUpperCase()}
                            </div>
                            {user.username}
                            {user.id === userId && <span className="text-[10px] text-[#8C8C89]">(active)</span>}
                          </div>
                          <button
                            onClick={(e) => handleRemoveUser(user, e)}
                            className="p-1 text-[#8C8C89] hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator className="bg-[rgba(0,0,0,0.08)]" />
                      <DropdownMenuItem onClick={() => setAddUserOpen(true)} className="font-mono text-sm text-[#D97706] cursor-pointer">
                        <UserPlus className="w-4 h-4 mr-2" />
                        Add User
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={logoutAll} className="font-mono text-sm text-red-500 cursor-pointer">
                        <X className="w-4 h-4 mr-2" />
                        Clear All Users
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <div className="pt-4 border-t border-[rgba(0,0,0,0.08)] space-y-2">
                    <button
                      onClick={() => setCreateOpen(true)}
                      className="w-full h-10 retro-btn-solid font-mono text-sm flex items-center justify-center gap-2"
                      data-testid="create-lobby-btn"
                    >
                      <Plus className="w-4 h-4" />
                      CREATE LOBBY
                    </button>
                    <button
                      onClick={() => setJoinOpen(true)}
                      className="w-full h-10 retro-btn font-mono text-sm flex items-center justify-center gap-2"
                      data-testid="join-lobby-btn"
                    >
                      <Lock className="w-4 h-4" />
                      JOIN LOBBY
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* My Lobbies */}
            {isLoggedIn && myLobbies.length > 0 && (
              <div className="retro-box p-5 mt-4">
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[rgba(0,0,0,0.08)]">
                  <Dumbbell className="w-4 h-4 text-[#D97706]" />
                  <span className="font-mono text-xs uppercase tracking-wider text-[#8C8C89]">
                    MY LOBBIES ({myLobbies.length})
                  </span>
                </div>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {myLobbies.map((lobby) => (
                    <button
                      key={lobby.id}
                      onClick={() => handleEnterLobby(lobby.id)}
                      className="w-full p-3 bg-[#FAF9F7] border border-[rgba(0,0,0,0.08)] hover:border-[#D97706] transition-colors text-left group"
                      data-testid={`my-lobby-${lobby.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-sm text-[#1F1F1E] group-hover:text-[#D97706] transition-colors">
                          {lobby.name}
                        </span>
                        <ChevronRight className="w-4 h-4 text-[#8C8C89] group-hover:text-[#D97706] transition-colors" />
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Users className="w-3 h-3 text-[#8C8C89]" />
                        <span className="font-mono text-xs text-[#8C8C89]">{lobby.member_count}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - All Lobbies */}
          <div className="lg:col-span-8">
            <div className="retro-box p-5">
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-[rgba(0,0,0,0.08)]">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#D97706]" />
                  <span className="font-mono text-xs uppercase tracking-wider text-[#8C8C89]">
                    ALL LOBBIES
                  </span>
                </div>
                <span className="font-mono text-xs text-[#8C8C89]">{lobbies.length} active</span>
              </div>

              {lobbies.length === 0 ? (
                <div className="py-12 text-center">
                  <Terminal className="w-10 h-10 mx-auto mb-3 text-[#8C8C89]" />
                  <p className="font-mono text-sm text-[#5C5C5A]">NO ACTIVE LOBBIES</p>
                  <p className="font-mono text-xs text-[#8C8C89] mt-1">Create one to get started</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {lobbies.map((lobby) => {
                    const isMember = myLobbies.some(l => l.id === lobby.id);
                    return (
                      <div
                        key={lobby.id}
                        className="p-4 bg-[#FAF9F7] border border-[rgba(0,0,0,0.08)] hover:border-[rgba(217,119,6,0.4)] transition-colors group"
                        data-testid={`lobby-${lobby.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-['VT323'] text-xl text-[#1F1F1E]">
                                {lobby.name}
                              </span>
                              {isMember && (
                                <span className="font-mono text-[10px] px-1.5 py-0.5 bg-[#D97706]/15 text-[#D97706] border border-[#D97706]/30">
                                  JOINED
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-2 font-mono text-xs text-[#8C8C89]">
                              <div className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                <span>{lobby.member_count} members</span>
                              </div>
                              <span>by {lobby.owner_username}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => copyLobbyId(lobby.id, e)}
                              className="p-2 text-[#8C8C89] hover:text-[#D97706] transition-colors"
                              title="Copy ID"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            {isLoggedIn && (
                              isMember ? (
                                <button
                                  onClick={() => handleEnterLobby(lobby.id)}
                                  className="px-3 py-1.5 retro-btn-solid font-mono text-xs"
                                >
                                  ENTER
                                </button>
                              ) : (
                                <button
                                  onClick={() => {
                                    setJoinForm({ ...joinForm, lobbyId: lobby.id });
                                    setJoinOpen(true);
                                  }}
                                  className="px-3 py-1.5 retro-btn font-mono text-xs"
                                >
                                  JOIN
                                </button>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-8 pt-4 border-t border-[rgba(0,0,0,0.08)] text-center">
          <p className="font-mono text-xs text-[#8C8C89]">
            Working.exe © 2026 | Track. Compete. Dominate.
          </p>
        </footer>
      </div>

      {/* Add User Dialog */}
      <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
        <DialogContent className="bg-white border-[rgba(217,119,6,0.3)] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['VT323'] text-2xl text-[#D97706]">ADD USER</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4 mt-4">
            <Input
              value={newUserName}
              onChange={(e) => setNewUserName(e.target.value)}
              placeholder="enter name..."
              className="h-11 bg-[#FAF9F7] border-[rgba(0,0,0,0.15)] text-[#D97706] font-mono"
            />
            <button type="submit" disabled={loading} className="w-full h-11 retro-btn-solid font-mono text-sm">
              {loading ? "ADDING..." : "[ADD USER]"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Lobby Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white border-[rgba(217,119,6,0.3)] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['VT323'] text-2xl text-[#D97706]">CREATE LOBBY</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateLobby} className="space-y-4 mt-4">
            <div>
              <label className="block font-mono text-xs text-[#8C8C89] mb-2">{">"} lobby_name</label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="enter name..."
                className="h-11 bg-[#FAF9F7] border-[rgba(0,0,0,0.15)] text-[#D97706] font-mono"
              />
            </div>
            <div>
              <label className="block font-mono text-xs text-[#8C8C89] mb-2">{">"} password</label>
              <Input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                placeholder="set password..."
                className="h-11 bg-[#FAF9F7] border-[rgba(0,0,0,0.15)] text-[#D97706] font-mono"
              />
            </div>
            <button type="submit" disabled={submitting} className="w-full h-11 retro-btn-solid font-mono text-sm">
              {submitting ? "CREATING..." : "[CREATE]"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Join Lobby Dialog */}
      <Dialog open={joinOpen} onOpenChange={setJoinOpen}>
        <DialogContent className="bg-white border-[rgba(217,119,6,0.3)] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['VT323'] text-2xl text-[#D97706]">JOIN LOBBY</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleJoinLobby} className="space-y-4 mt-4">
            <div>
              <label className="block font-mono text-xs text-[#8C8C89] mb-2">{">"} lobby_id</label>
              <Input
                value={joinForm.lobbyId}
                onChange={(e) => setJoinForm({ ...joinForm, lobbyId: e.target.value })}
                placeholder="paste lobby id..."
                className="h-11 bg-[#FAF9F7] border-[rgba(0,0,0,0.15)] text-[#D97706] font-mono"
              />
            </div>
            <div>
              <label className="block font-mono text-xs text-[#8C8C89] mb-2">{">"} password</label>
              <Input
                type="password"
                value={joinForm.password}
                onChange={(e) => setJoinForm({ ...joinForm, password: e.target.value })}
                placeholder="enter password..."
                className="h-11 bg-[#FAF9F7] border-[rgba(0,0,0,0.15)] text-[#D97706] font-mono"
              />
            </div>
            <button type="submit" disabled={submitting} className="w-full h-11 retro-btn-solid font-mono text-sm">
              {submitting ? "JOINING..." : "[JOIN]"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MainPage;
